<template>
  <div>
    <Modal
      v-model:open="open"
      data-testid="consignment-form-modal"
      width="900px"
      :title="stepNames[currentStepIdx]!"
      @update:open="close"
      @closed="close"
    >
      <!-- Creation Form -->
      <template v-if="validationSchema">
        <form
          v-show="!consignment"
          id="shipment-form"
          :key="formKey"
          data-testid="shipment-form"
          @submit.prevent="onSubmit"
        >
          <FormStep>
            <ReceiverSender
              mode="receiver"
              type="consignments"
              :values="values"
              @update-city="setReceiverSenderCity"
              @emit-contact="setReceiverSender"
              @update-address="(val) => setReceiverSender(val, true)"
              @update-validation="handleUpdateValidation"
            />
          </FormStep>
          <FormStep>
            <Dimensions />
          </FormStep>
        </form>
      </template>
      <!-- Success Screen -->
      <div
        v-if="!!consignment"
        class="mx-auto my-auto flex min-h-[540px] max-w-[540px] -translate-x-1 transform flex-col justify-center"
        data-step-key="success"
      >
        <div class="flex flex-col items-center">
          <SuccessBlob />
          <p class="text-title-section mb-2 w-full text-center">
            {{ t("consignment_is_created") }}
          </p>
          <div class="mb-2 flex items-center justify-center gap-2">
            <Button
              :variant="ButtonVariant.Tertiary"
              type="button"
              @click="() => closeAndOpenConsignment(consignment.id)"
            >
              {{ t("open_entity", { entity: t("consignment").toLowerCase() }) }}
              <template #suffix>
                <Icon src="ArrowUpRight" />
              </template>
            </Button>
          </div>
        </div>
        <!-- Label Download/Print Options -->
        <div class="flex max-h-82 flex-col gap-2 overflow-auto">
          <div
            v-for="cp in consignment?.response_data?.at(0)?.packages"
            :key="cp.package_number"
            class="border-bdr-subtle flex w-full items-center gap-2 rounded-xl border px-4 py-0.5"
          >
            <Icon src="file_text" />
            <p class="text-body-default">{{ cp.package_number }}.pdf</p>
            <div class="ml-auto flex items-center">
              <Button :variant="ButtonVariant.Tertiary" target="_blank" :href="`/shipments/${cp.package_number}/label`">
                {{ t("download") }}
              </Button>
              <Button
                type="button"
                :variant="ButtonVariant.Tertiary"
                @click="() => openPrinterModal(cp.package_number)"
              >
                {{ t("print_entity") }}
              </Button>
            </div>
          </div>
        </div>

        <!-- Success Screen Buttons -->
        <div class="mt-4 flex w-full flex-col gap-2">
          <Button class="w-full" type="button" :variant="ButtonVariant.Secondary" @click="createNewConsignment">
            {{ t("create_another_consignment") }}
          </Button>
          <Button type="button" :variant="ButtonVariant.Tertiary" @click="() => openPrinterModal()">
            {{ t("print_all_prebooking_labels", { n: consignment?.response_data?.at(0)?.packages?.length }) }}
          </Button>
        </div>
      </div>

      <template v-if="validationSchema && !consignment" #footer>
        <ModalFooter class="px-1.5">
          <Button
            v-if="hasPrevious"
            class="w-full max-w-max"
            type="button"
            :variant="ButtonVariant.Tertiary"
            @click="goToPrev"
            >{{ t("previous") }}</Button
          >
          <Button
            v-if="hasContinueButton"
            class="ms-auto w-full max-w-max"
            type="submit"
            :disabled="!canSubmit"
            :variant="isLastStep ? ButtonVariant.Positive : ButtonVariant.Primary"
            :loading="loading || buttonLoading"
            form="shipment-form"
          >
            {{ continueButtonText }}
          </Button>
        </ModalFooter>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
  import ReceiverSender from "~~/modules/newshipment/runtime/components/steps/receiver_sender.vue";
  import { createFormUIControls, goToPrevStep } from "~~/modules/newshipment/runtime/helpers/formManagement";
  import { saveContact } from "~~/modules/newshipment/runtime/helpers/shipmentOperations";

  import { registerConsignmentEvents, submitConsignmentForm } from "../helpers/eventHandlers";
  import { loading, open } from "../helpers/service";
  import Dimensions from "./steps/dimensions.vue";

  defineOptions({
    name: "NewConsignmentModal",
  });

  const { t } = useI18n();
  const yup = useYup();
  const { injectValidationFields } = useValidation();

  const buttonLoading = ref(false);

  const buildValidationSchema = () => {
    return [
      yup
        .object({
          customer_contact_id: yup.string().nullable().default(null),
          save_receiver: yup.boolean().nullable(),
          receiver: yup.object().shape({
            name: yup.string().required().label(t("name")),
            email: yup.string().email().required().label(t("email")),
            phone: yup
              .string()
              .required()
              .test("has_prefix", t("phone_number_must_include_prefix"), (value) => !!(value && value.startsWith("+")))
              .label(t("phone_number"))
              .default("+45"),
            street1: yup.string().required().label(t("street1")),
            street2: yup.string().nullable().label(t("street2")),
            zip_code: yup.string().required().label(t("zip_code")),
            city: yup.string().required().label(t("city")),
            country: yup.string().required().label(t("country")).default("DK"),
            business: yup.boolean().required().default(true),
            company_name: yup.string().nullable().label(t("company_name")),
            state: yup.string().nullable(),
          }),
        })
        .meta({ continueButtonText: t("continue") }),
      yup
        .object({
          quantity: yup.number().required(),
          length: yup.number().required(),
          width: yup.number().required(),
          height: yup.number().nullable(),
        })
        .meta({ continueButtonText: t("continue") }),
    ];
  };

  // Using any due to complex Yup schema types with meta properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validationSchemaRef = ref<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validationSchema = computed<any[]>(() => validationSchemaRef.value);

  validationSchemaRef.value = buildValidationSchema();

  const currentStepIdx = ref(0);
  const stepCounter = ref(0);

  provide("STEP_COUNTER", stepCounter);
  provide("CURRENT_STEP_INDEX", currentStepIdx);

  const currentSchema = computed(() => {
    if (!validationSchema.value) {
      return {};
    }
    return validationSchema.value[currentStepIdx.value];
  });

  provide("CURRENT_STEP_SCHEMA", currentSchema);

  const initialValues = computed(() => {
    if (!validationSchema.value) {
      return {};
    }
    const entries = validationSchema.value.map((schema) => {
      const acc: Record<string, unknown> = {};
      Object.entries(schema.fields).forEach((field) => {
        const [key, value] = field;
        const typedValue = value as {
          fields?: Record<string, { spec: { default: unknown } }>;
          spec?: { default: unknown };
        };

        if (typedValue.fields) {
          acc[key] = {} as Record<string, unknown>;
          Object.entries(typedValue.fields).forEach((subField) => {
            const [subKey, subValue] = subField;
            (acc[key] as Record<string, unknown>)[subKey] = subValue.spec.default;
          });
        } else if (typedValue.spec) {
          acc[key] = typedValue.spec.default;
        }
      });
      return acc;
    });

    return entries.reduce((acc, entry) => {
      return { ...acc, ...entry };
    }, {});
  });

  const { values, handleSubmit, setValues, setFieldValue, resetForm, setErrors } = useForm({
    validationSchema: currentSchema,
    keepValuesOnUnmount: true,
    initialValues: initialValues.value,
  });

  provide("FORM_VALUES", values);

  const isValid = useIsFormValid();
  const isDirty = useIsFormDirty();

  const { isLastStep, hasPrevious, hasContinueButton, continueButtonText, canSubmit } = createFormUIControls(
    currentStepIdx,
    stepCounter,
    currentSchema,
    isValid,
    isDirty,
    values,
    t
  );

  const stepNames = [t("set_receiver"), t("specify_dimensions")];

  const consignment = ref<Record<string, unknown> | null>(null);
  const formKey = ref(0);

  onMounted(() => {
    const events = registerConsignmentEvents(EventBus, setSender);
    events.register();
    setFieldValue("save_receiver", false);
  });

  onUnmounted(() => {
    const events = registerConsignmentEvents(EventBus, setSender);
    events.unregister();
  });

  const onSubmit = handleSubmit(async (v) => {
    // Save contact if needed
    if (currentStepIdx.value === 0) {
      if (v.save_receiver === undefined) {
        setFieldValue("save_receiver", false);
      }
      if (v.save_receiver) {
        await saveContact(v.receiver);
        setFieldValue("save_receiver", false);
      }
    }

    if (!isLastStep.value && validationSchema.value) {
      for (let i = currentStepIdx.value + 1; i < validationSchema.value.length; i++) {
        if (!validationSchema.value[i]?.spec?.meta?.autoskip) {
          currentStepIdx.value = i;
          return;
        }
      }
    }
    if (isLastStep.value) {
      submitForm(v);
    }
  });

  provide("SUBMIT_STEP", onSubmit);

  function goToPrev() {
    goToPrevStep(currentSchema, currentStepIdx, validationSchema);
  }

  const close = () => {
    consignment.value = null;
    currentStepIdx.value = 0;
    stepCounter.value = 0;
    resetForm();
    open.value = false;
    loading.value = false;
  };

  const createNewConsignment = () => {
    consignment.value = null;
    currentStepIdx.value = 0;
    stepCounter.value = 0;
    resetForm();

    formKey.value++;
  };

  const handleUpdateValidation = (val: { fields: object; step: number }) => {
    injectValidationFields(val.fields, validationSchema.value, val.step, "receiver");
  };

  const setSender = () => {
    useCustomer()
      .fetchCustomer()
      .then((customerData) => {
        if (customerData.data) {
          const data = customerData.data as Record<string, unknown>;
          const mappedData = {
            city: data.city || "",
            country: data.country_iso || "",
            email: data.email || "",
            name: data.name || "",
            attention: data.attention || "",
            phone: data.phone || "",
            phone_prefix: data.phone_prefix || "",
            street1: data.street1 || "",
            street2: data.street2 || "",
            zip_code: data.zip_code || "",
            business: true,
          };

          setValues({
            sender: mappedData,
          });
          resetForm();
        }
      });
  };

  const setReceiverSender = (contact: { data: Record<string, unknown> }, reverse = false) => {
    const key = reverse ? "sender" : "receiver";
    contact.data.business = contact.data.business ?? !!contact.data.company_name;

    setValues({
      [key]: {
        name: contact.data.business ? contact.data.company_name : contact.data.name,
        attention: contact.data.business ? (contact.data.attention ?? contact.data.name) : undefined,
        email: contact.data.email,
        phone_prefix: contact.data.phone_prefix,
        phone: contact.data.phone,
        street1: contact.data.street1,
        street2: contact.data.street2,
        zip_code: contact.data.zip_code,
        city: contact.data.city,
        country: contact.data.country_code,
        is_business: true,
      },
      customer_contact_id: contact.data.id || null,
    });
  };

  /**
   * Updates city field for receiver/sender
   */
  const setReceiverSenderCity = (city: string) => {
    const fieldKey = "receiver";
    setFieldValue(`${fieldKey}.city`, city as never);
  };

  const closeAndOpenConsignment = (id: number) => {
    close();
    EventBus?.$emit("OPEN_CONSIGNMENT", id);
  };

  const openPrinterModal = (packageNumber?: string) => {
    if (packageNumber) printingAddLabel(packageNumber);
    else printingAddConsignment(consignment.value?.consignment_number as string);
    printingOpen();
  };

  const submitForm = async (formData: unknown) => {
    try {
      const result = await submitConsignmentForm(formData, homeFetch);
      consignment.value = result?.data?.consignment;
      buttonLoading.value = false;
      loading.value = false;
    } catch (e) {
      buttonLoading.value = false;
      loading.value = false;
    }
  };
  defineExpose({
    values,
    setValues,
    setFieldValue,
    resetForm,
    setErrors,
  });
</script>
